import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n/index.js";
import { CloseIcon, DownloadIcon, LinkIcon, ShareIcon } from "./icons";
import { cn } from "../lib/cn";

/**
 * A photograph, at the size of the screen (SPEC §4.5).
 *
 * The thumbnails used to be plain links, and on a phone that meant the picture
 * left the app: it opened in the in-app browser, which shows a photo and offers
 * nothing to do with it. «Скопировать, скачать и так далее» were all one layer
 * away, behind a chrome nobody asked for.
 *
 * So the row of actions at the bottom is the point of this component, and
 * **«Поделиться» is the one that matters**. `navigator.share` with a file opens
 * the system sheet, and the system sheet is where "Сохранить в фото",
 * "Скопировать" and AirDrop actually live — writing our own versions of those
 * would be three worse buttons. `download` and «Открыть в браузере» are the
 * ways out where there is no sheet, which is most desktop browsers.
 *
 * The URL and the type come from `useMedia` already resolved: in an encrypted
 * space that is a `blob:` of bytes this device has already opened, and opening
 * them a second time here would be both slower and wrong. The blob belongs to
 * the hook, so nothing here revokes it.
 */

/*
 * The one component in the app pinned to a theme rather than following it.
 *
 * A viewer is a photograph and nothing else, and a light frame around a
 * photograph lies about its colours — every eye reads the picture against what
 * surrounds it. So the ground stays black in both themes, and the chrome on top
 * of it cannot use the surface tokens, which flip. These two are the dark
 * theme's own `--color-accent`, `--color-ground` and `--color-neg` from
 * `styles.css`: the same three colours the app already uses, just no longer
 * allowed to change under a light theme.
 */
const AMBER = "bg-[#e9a94e] text-[#14110e]";
const NEGATIVE = "text-[#e0918a]";

const ACTION =
  "inline-flex min-h-[var(--touch)] items-center justify-center gap-2 rounded-pill px-3 font-medium transition-colors";
const QUIET = "bg-white/10 text-white/90 hover:bg-white/15";

export interface MediaViewerProps {
  /** Ready for `src` — the address `useMedia` handed out, opened if it needed it. */
  url: string;
  /** The decrypted name. What the share sheet and `download` will call the file. */
  name: string;
  /** The real type, from inside the envelope where there is one. */
  mime: string;
  onClose: () => void;
  /** The thumbnail this was opened from, to give focus back to. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * A share sheet that was simply closed.
 *
 * `navigator.share` rejects with `AbortError` for that exactly as it does for a
 * real failure, and backing out of a sheet is not a failure — it is the answer
 * "not this one". Checked by name rather than `instanceof DOMException`,
 * because what a browser throws here is not guaranteed to be one.
 */
function isAbort(cause: unknown): boolean {
  return (
    typeof cause === "object" &&
    cause !== null &&
    (cause as { name?: unknown }).name === "AbortError"
  );
}

/**
 * Whether this device's share sheet takes files at all.
 *
 * Asked with an empty stand-in of the right name and type: a browser refuses
 * files by type — most desktop ones refuse them altogether — so the answer is
 * the same one the real bytes would get, without downloading them to find out.
 * A button that offers a sheet that is not there is worse than no button.
 */
function canShareFile(name: string, mime: string): boolean {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") return false;
  try {
    return navigator.canShare?.({ files: [new File([], name, { type: mime })] }) === true;
  } catch {
    return false;
  }
}

async function toFile(
  url: string,
  name: string,
  mime: string,
  signal?: AbortSignal,
): Promise<File> {
  const response = await fetch(url, {
    // The same credential every other request carries: the media route is on
    // our own origin behind the session cookie (SPEC §8). Ignored, harmlessly,
    // for the `blob:` of an already-opened file.
    credentials: "same-origin",
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) throw new Error(`media ${response.status}`);

  const blob = await response.blob();
  return new File([blob], name, { type: mime || blob.type });
}

export function MediaViewer({ url, name, mime, onClose, returnFocusTo }: MediaViewerProps) {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const pending = useRef<Promise<File> | null>(null);
  const [sharing, setSharing] = useState(false);
  const [failed, setFailed] = useState(false);

  const shareable = useMemo(() => canShareFile(name, mime), [name, mime]);

  useEffect(() => {
    if (!shareable) return;

    /*
     * The bytes are fetched when the viewer opens, not when the button is
     * pressed.
     *
     * `navigator.share` has to be called while the tap is still the browser's
     * current gesture, and a network round trip in the middle is precisely what
     * ends it — iOS then refuses the sheet. Starting here means the press has
     * nothing left to wait for but a microtask.
     */
    const aborter = new AbortController();
    const work = toFile(url, name, mime, aborter.signal);
    // Nobody may be awaiting this yet, and an unhandled rejection is noise: the
    // press is what reports a failure, and it awaits the same promise.
    work.catch(() => {});
    pending.current = work;

    return () => {
      pending.current = null;
      aborter.abort();
    };
  }, [shareable, url, name, mime]);

  // Read through a ref so the listener and the lock are installed once. A
  // handler in the deps is a new function on every render of the strip, and the
  // scroll lock would be taken and given back with each of them.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== "Escape") return;
      keyEvent.preventDefault();
      closeRef.current();
    };

    // The page underneath stays where it was rather than scrolling away behind
    // a picture that covers it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    dialogRef.current?.focus();

    // Back onto the thumbnail on the way out. A keyboard that landed at the top
    // of the page instead would have lost its place in the strip.
    return () => returnFocusTo?.current?.focus();
  }, [returnFocusTo]);

  async function share() {
    setSharing(true);
    setFailed(false);
    try {
      const file = await (pending.current ?? toFile(url, name, mime));
      await navigator.share({ files: [file] });
    } catch (cause) {
      // A dismissed sheet says nothing, because nothing went wrong.
      if (isAbort(cause)) return;
      // The prefetch is what failed as often as not, and a rejected promise
      // held on to would fail every later press without trying again.
      pending.current = null;
      setFailed(true);
    } finally {
      setSharing(false);
    }
  }

  return createPortal(
    <>
      {/* Black rather than a token — see the note at the top of the file. It is
          also what catches a tap beside the picture: the layer above lets one
          through everywhere it draws nothing. */}
      <div onClick={onClose} aria-hidden="true" className="fixed inset-0 z-50 bg-black/95" />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("attachments.viewing", { name })}
        tabIndex={-1}
        className="pad-safe-x pointer-events-none fixed inset-0 z-50 flex flex-col outline-none"
      >
        <header
          className="pointer-events-auto flex items-center gap-1 px-2"
          style={{ paddingTop: "calc(var(--safe-top) + 0.5rem)" }}
        >
          <p className="min-w-0 flex-1 truncate px-2 text-[0.8125rem] text-white/70">{name}</p>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <CloseIcon className="size-[22px]" strokeWidth={1.9} />
          </button>
        </header>

        {/* `min-h-0` so this is the part that gives way when the bars take their
            height, rather than the picture pushing them off the screen. */}
        <div className="flex min-h-0 flex-1 items-center justify-center px-3 py-2">
          <img
            src={url}
            alt={name}
            decoding="async"
            className="pointer-events-auto max-h-full max-w-full object-contain"
          />
        </div>

        <div
          className="pointer-events-auto mx-auto flex w-full max-w-md flex-col gap-2 px-3"
          style={{ paddingBottom: "calc(var(--safe-bottom) + 0.75rem)" }}
        >
          {failed ? (
            <p role="alert" className={cn("text-center text-[0.8125rem]", NEGATIVE)}>
              {t("attachments.shareFailed")}
            </p>
          ) : null}

          {shareable ? (
            <button
              type="button"
              onClick={() => void share()}
              disabled={sharing}
              className={cn(ACTION, AMBER, "text-[0.9375rem] disabled:opacity-60")}
            >
              <ShareIcon className="size-[19px]" strokeWidth={1.9} />
              {t("attachments.share")}
            </button>
          ) : null}

          <div className="flex gap-2">
            <a
              href={url}
              download={name}
              className={cn(ACTION, QUIET, "min-w-0 flex-1 text-[0.8125rem]")}
            >
              <DownloadIcon className="size-[18px]" strokeWidth={1.9} />
              <span className="truncate">{t("attachments.save")}</span>
            </a>

            {/* The way out when neither of the two above is honoured — Safari
                has ignored `download` before, and a desktop has no sheet. */}
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={cn(ACTION, QUIET, "min-w-0 flex-1 text-[0.8125rem]")}
            >
              <LinkIcon className="size-[18px]" strokeWidth={1.9} />
              <span className="truncate">{t("attachments.openInBrowser")}</span>
            </a>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
